import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect, useCallback, useRef, type ReactNode } from "react";

interface HeroSlide {
  /** Base path for a responsive set: `${imageBase}-mobile.webp`, `.webp`, `.jpg`. */
  imageBase?: string;
  /** Single source, for images that have no responsive variants. */
  image?: string;
  alt: string;
  badge: string;
  /** Coral reads as the brand accent; light suits slides whose image is already dark. */
  badgeTone?: "coral" | "light";
  heading: ReactNode;
  cta: { text: string; link: string };
  /** `object-position`, to move the crop window off dead centre. See FOCAL below. */
  focal?: string;
}

// The slider is far wider than it is tall (~2.5:1) while the source photos are
// 3:2, so `object-cover` has to discard a good chunk of their height. Centring
// that crop cuts off heads, because the subjects sit high in every frame — so
// the window is biased upward by default.
const FOCAL = "50% 28%";

// Kept in one place: the slider box and the copy overlay must stay the same
// height or the copy stops sitting on the image.
const HERO_HEIGHT = "min-h-[380px] md:min-h-[600px] lg:min-h-[680px]";

// One horizontal slider rather than the previous three-panel bento. The two
// secondary tiles (marketplace, Eid) became slides 3 and 4 so their imagery and
// links survive the change instead of being dropped.
const heroSlides: HeroSlide[] = [
  {
    imageBase: "/images/hero-new-arrivals",
    alt: "Three women in elegant modest fashion – hijab styles with burgundy, beige, and sage green outfits",
    badge: "New arrivals",
    badgeTone: "coral",
    heading: <>Your hijabi shop, all in one place.</>,
    cta: { text: "Shop now", link: "/shop" },
  },
  {
    imageBase: "/images/hero-modest-activewear",
    alt: "Woman in hijab at the gym with activewear",
    badge: "Modest activewear",
    badgeTone: "light",
    heading: <>Modest activewear that performs.</>,
    cta: { text: "Shop activewear", link: "/category/activewear" },
  },
  {
    image: "/lovable-uploads/223cc82c-3712-4394-85b2-fe9ed0c70f6b.jpg",
    alt: "Modest fashion marketplace US — discover hijabs and abayas from verified sellers",
    badge: "Marketplace",
    badgeTone: "coral",
    heading: <>Discover hijab and abaya brands you'll love.</>,
    cta: { text: "Shop the marketplace", link: "/shop" },
  },
  {
    imageBase: "/images/hero-ramadan-shop",
    alt: "Eid fashion and modest occasion wear for Islamic celebrations",
    badge: "Curated for you",
    badgeTone: "light",
    heading: <>Eid &amp; Beyond Collection</>,
    cta: { text: "Shop occasion wear", link: "/shop?collection=occasion" },
  },
];

const SlideImage = ({ slide, isLCP }: { slide: HeroSlide; isLCP: boolean }) => {
  const imgProps = {
    alt: slide.alt,
    className: "absolute inset-0 w-full h-full object-cover",
    style: { objectPosition: slide.focal ?? FOCAL },
    width: 1600,
    height: 640,
    decoding: "async" as const,
    fetchPriority: isLCP ? ("high" as const) : undefined,
    loading: isLCP ? undefined : ("lazy" as const),
  };

  if (!slide.imageBase) {
    return <img src={slide.image} {...imgProps} />;
  }

  return (
    <picture>
      <source media="(max-width: 767px)" srcSet={`${slide.imageBase}-mobile.webp`} type="image/webp" />
      <source srcSet={`${slide.imageBase}.webp`} type="image/webp" />
      <img src={`${slide.imageBase}.jpg`} {...imgProps} />
    </picture>
  );
};

const HeroBento = () => {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef<number | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = useCallback((idx: number) => {
    setCurrent((idx + heroSlides.length) % heroSlides.length);
  }, []);

  useEffect(() => {
    // Delay auto-rotate so the LCP image candidate stays stable for measurement.
    const startTimeout = setTimeout(() => {
      intervalRef.current = setInterval(() => setCurrent(c => (c + 1) % heroSlides.length), 5000);
    }, 5000);
    return () => {
      clearTimeout(startTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const resetTimer = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => setCurrent(c => (c + 1) % heroSlides.length), 5000);
  }, []);

  const active = heroSlides[current];

  return (
    // Full-bleed: no `container` wrapper, so the slider spans the whole viewport
    // width, square-cornered and flush with the dark band beneath it. The copy
    // inside still sits in a container so it stays on the site's grid rather
    // than drifting to the screen edge on wide monitors.
    <section className="py-0" aria-labelledby="hero-heading">
        <div
          className={`relative overflow-hidden ${HERO_HEIGHT}`}
          onTouchStart={e => { touchStart.current = e.touches[0].clientX; }}
          onTouchEnd={e => {
            if (touchStart.current === null) return;
            const diff = touchStart.current - e.changedTouches[0].clientX;
            if (Math.abs(diff) > 50) {
              goTo(diff > 0 ? current + 1 : current - 1);
              resetTimer();
            }
            touchStart.current = null;
          }}
        >
          {heroSlides.map((slide, i) => (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-700 ${i === current ? "opacity-100 z-[1]" : "opacity-0 z-0"}`}
              aria-hidden={i !== current}
            >
              <SlideImage slide={slide} isLCP={i === 0} />
              {/* Scrim on every slide, not just the first: the copy sits over
                  whatever photo is showing, so legibility can't depend on which
                  one happens to be active. */}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/35 to-transparent" />
            </div>
          ))}

          <div className={`relative z-10 flex items-end ${HERO_HEIGHT}`}>
            {/* Image is full-bleed; the copy stays on the site grid. */}
            <div className="container mx-auto px-4 py-8 md:py-14">
              <div className="flex flex-col max-w-2xl">
            <span
              className={`inline-block self-start text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full mb-3 md:mb-4 ${
                active.badgeTone === "light" ? "bg-white/90 text-charcoal" : "bg-[#E87266] text-white"
              }`}
            >
              {active.badge}
            </span>

            <h2
              id="hero-heading"
              className="font-serif text-[26px] md:text-5xl lg:text-6xl text-white mb-4 md:mb-6 leading-[1.1] hero-text-shadow"
            >
              {active.heading}
            </h2>

            <Button
              asChild
              className="self-start rounded-full bg-gradient-to-r from-[hsl(5_72%_58%)] to-[hsl(340_65%_62%)] hover:brightness-105 text-white px-6 h-11 text-sm font-semibold gap-2 shadow-[0_10px_25px_-8px_rgba(196,90,90,0.6)]"
            >
              <Link to={active.cta.link}>
                {active.cta.text} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>

            {/* Dots */}
            <div className="flex gap-2 mt-6">
              {heroSlides.map((slide, i) => (
                <button
                  key={i}
                  onClick={() => { goTo(i); resetTimer(); }}
                  className={`h-2 rounded-full transition-all ${i === current ? "bg-white w-6" : "bg-white/50 w-2 hover:bg-white/75"}`}
                  aria-label={`Show slide ${i + 1}: ${slide.badge}`}
                  aria-current={i === current}
                />
              ))}
            </div>
              </div>
            </div>
          </div>
        </div>
    </section>
  );
};

export default HeroBento;
